// src/services/nftFetcher/nftFormatUtils.js

/**
 * Standardize NFT format across different chain APIs
 * This ensures all NFT objects have consistent structure regardless of the source API
 * 
 * @param {Object} nftData - Raw NFT data from chain API
 * @param {string} chainKey - The chain identifier
 * @returns {Object} - Standardized NFT object
 */
export const standardizeNFTFormat = (nftData, chainKey) => {
  // Base NFT structure
  const standardNFT = {
    nft: {
      type: "ERC-721",
      contractAddress: null,
      tokenId: null,
      name: null,
      description: null,
      image: null,
      imageUrl: null,
      animationUrl: null,
      externalUrl: null,
      attributes: [],
      collectionName: null,
      tokenUri: null,
      chainKey: chainKey
    },
    metadata: {
      lastUpdated: null,
      fetchedAt: new Date().toISOString()
    }
  };

  // Handle different API response formats
  try {
    // Ethereum/Routescan actual format
    if (nftData.tokenAddress && nftData.tokenId && nftData.chainId) {
      standardNFT.nft.contractAddress = nftData.tokenAddress;
      standardNFT.nft.tokenId = nftData.tokenId;
      standardNFT.nft.collectionName = nftData.collectionName;
      standardNFT.nft.name = `${nftData.collectionName || 'NFT'} #${nftData.tokenId}`;
      
      // Handle collection symbol
      if (nftData.collectionSymbol) {
        standardNFT.nft.symbol = nftData.collectionSymbol;
      }
      
      // Handle fetched metadata (from OpenSea or other sources)
      if (nftData.metadata) {
        const metadata = typeof nftData.metadata === 'string' 
          ? JSON.parse(nftData.metadata) 
          : nftData.metadata;
          
        // Override name if metadata provides a better one
        if (metadata.name) {
          standardNFT.nft.name = metadata.name;
        }
        
        standardNFT.nft.description = metadata.description;
        standardNFT.nft.image = metadata.image;
        standardNFT.nft.imageUrl = metadata.image_url || metadata.image;
        standardNFT.nft.animationUrl = metadata.animation_url;
        standardNFT.nft.externalUrl = metadata.external_url;
        standardNFT.nft.attributes = metadata.attributes || [];
      }
    }
    // Legacy Ethereum/Routescan format (if they change back)
    else if (nftData.tokenAddress && nftData.tokenId && nftData.tokenName) {
      standardNFT.nft.contractAddress = nftData.tokenAddress;
      standardNFT.nft.tokenId = nftData.tokenId;
      standardNFT.nft.name = nftData.tokenName || nftData.name;
      standardNFT.nft.collectionName = nftData.tokenName;
      
      // Handle metadata if available
      if (nftData.metadata) {
        const metadata = typeof nftData.metadata === 'string' 
          ? JSON.parse(nftData.metadata) 
          : nftData.metadata;
          
        standardNFT.nft.name = metadata.name || standardNFT.nft.name;
        standardNFT.nft.description = metadata.description;
        standardNFT.nft.image = metadata.image;
        standardNFT.nft.imageUrl = metadata.image_url || metadata.image;
        standardNFT.nft.animationUrl = metadata.animation_url;
        standardNFT.nft.externalUrl = metadata.external_url;
        standardNFT.nft.attributes = metadata.attributes || [];
      }
    }
    
    // Bitrock format
    else if (nftData.token && nftData.token.address) {
      standardNFT.nft.contractAddress = nftData.token.address;
      standardNFT.nft.tokenId = nftData.token_id || nftData.tokenId;
      standardNFT.nft.name = nftData.token.name;
      standardNFT.nft.collectionName = nftData.token.name;
      
      // For Bitrock, if we don't have a specific name and it's a virtual token ID,
      // create a more descriptive name
      if (!standardNFT.nft.name || standardNFT.nft.name === standardNFT.nft.collectionName) {
        standardNFT.nft.name = `${standardNFT.nft.collectionName || 'NFT'} #${standardNFT.nft.tokenId}`;
      }
      
      // Handle Bitrock metadata
      if (nftData.token.metadata) {
        const metadata = typeof nftData.token.metadata === 'string' 
          ? JSON.parse(nftData.token.metadata) 
          : nftData.token.metadata;
          
        standardNFT.nft.name = metadata.name || standardNFT.nft.name;
        standardNFT.nft.description = metadata.description;
        standardNFT.nft.image = metadata.image;
        standardNFT.nft.imageUrl = metadata.image_url || metadata.image;
        standardNFT.nft.animationUrl = metadata.animation_url;
        standardNFT.nft.externalUrl = metadata.external_url;
        standardNFT.nft.attributes = metadata.attributes || [];
      }
    }
    
    // Generic format handling
    else {
      standardNFT.nft.contractAddress = nftData.contract_address || nftData.address;
      standardNFT.nft.tokenId = nftData.token_id || nftData.id;
      standardNFT.nft.name = nftData.name;
      standardNFT.nft.description = nftData.description;
      standardNFT.nft.image = nftData.image;
      standardNFT.nft.imageUrl = nftData.image_url || nftData.image;
      standardNFT.nft.animationUrl = nftData.animation_url;
      standardNFT.nft.externalUrl = nftData.external_url;
      standardNFT.nft.attributes = nftData.attributes || [];
      standardNFT.nft.collectionName = nftData.collection_name;
    }

    // Ensure we have required fields
    if (!standardNFT.nft.contractAddress || !standardNFT.nft.tokenId) {
      console.warn('Invalid NFT data: missing contract address or token ID', nftData);
      return null;
    }

    // Clean up image URLs
    if (standardNFT.nft.imageUrl) {
      standardNFT.nft.imageUrl = cleanImageUrl(standardNFT.nft.imageUrl);
    }
    if (standardNFT.nft.image && !standardNFT.nft.imageUrl) {
      standardNFT.nft.imageUrl = cleanImageUrl(standardNFT.nft.image);
    }

    return standardNFT;
  } catch (error) {
    console.error('Error standardizing NFT format:', error, nftData);
    return null;
  }
};

/**
 * Clean and normalize image URLs
 * @param {string} imageUrl - Raw image URL
 * @returns {string} - Cleaned image URL
 */
const cleanImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // Handle IPFS URLs
  if (imageUrl.startsWith('ipfs://')) {
    return imageUrl.replace('ipfs://', 'https://ipfs.io/ipfs/');
  }
  
  // Handle relative URLs or data URLs
  if (imageUrl.startsWith('data:') || imageUrl.startsWith('http')) {
    return imageUrl;
  }
  
  // Handle gateway URLs
  if (imageUrl.includes('ipfs.io') || imageUrl.includes('gateway')) {
    return imageUrl;
  }
  
  return imageUrl;
};

/**
 * Generate a unique identifier for an NFT
 * @param {Object} nft - NFT object
 * @returns {string} - Unique identifier
 */
export const generateNFTId = (nft) => {
  if (!nft.contractAddress || !nft.tokenId) {
    return null;
  }
  return `${nft.contractAddress.toLowerCase()}-${nft.tokenId}`;
};

/**
 * Validate NFT object structure
 * @param {Object} nft - NFT object to validate
 * @returns {boolean} - Whether the NFT is valid
 */
export const isValidNFT = (nft) => {
  if (!nft || !nft.nft) return false;
  
  const { contractAddress, tokenId } = nft.nft;
  
  // For valid NFTs, we need at least a contract address
  // Token ID can be virtual (for Bitrock) or real
  return !!(contractAddress && (tokenId || tokenId === "0"));
};

/**
 * Sort NFTs by collection and token ID
 * @param {Array} nfts - Array of NFT objects
 * @returns {Array} - Sorted NFT array
 */
export const sortNFTs = (nfts) => {
  return nfts.sort((a, b) => {
    // First sort by collection name
    const collectionA = a.nft.collectionName || a.nft.name || '';
    const collectionB = b.nft.collectionName || b.nft.name || '';
    
    if (collectionA !== collectionB) {
      return collectionA.localeCompare(collectionB);
    }
    
    // Then sort by token ID (numeric if possible)
    const tokenIdA = parseInt(a.nft.tokenId) || 0;
    const tokenIdB = parseInt(b.nft.tokenId) || 0;
    
    return tokenIdA - tokenIdB;
  });
};